<template>
    <OpenableMenu>
        <template v-slot:label>
            Weergaveopties
        </template>
        <template v-slot:content>
            <List>
                <ListItem @click="$emit('open:themeSettings')">
                    <div>Thema- en kleurinstellingen</div>
                </ListItem>
                <ListItem @click.stop>
                    <CqToggle
                        v-model="showOnlyNegativeValues"
                        label="Toon alleen de negatieve paraatheid"
                    />
                </ListItem>
                <ListItem @click.stop>
                    <CqToggle
                        v-model="showFunctions"
                        label="Toon medewerkerfuncties"
                    />
                </ListItem>
                <ListItem @click.stop>
                    <CqToggle
                        v-model="showComments"
                        label="Toon medewerkeropmerkingen"
                    />
                </ListItem>
                <ListItem @click.stop>
                    <CqToggle
                        v-model="showStampStartDate"
                        label="Toon startdatum stempelfase"
                    />
                </ListItem>
                <ListItem @click.stop>
                    <CqToggle
                        v-model="showCompactMode"
                        label="Compacte weergave"
                    />
                </ListItem>
                <ListItem @click.stop>
                    <CqToggle
                        v-model="hideTooltip"
                        label="Cel tooltip verbergen"
                    />
                </ListItem>
                <ListItem @click.stop v-if="areWorkSchedulesEnabled">
                    <CqToggle
                        v-model="useNewWorkloadRequirements"
                        label="Nieuwe paraatheid gebruiken"
                    />
                </ListItem>
                <ListItem @click.stop>
                    <CqSelectInput noMargin label="Sorteer paraatheid op" :options="requirementSortingOptions" v-model="requirementSorting" />
                </ListItem>
                <ListItem @click.stop>
                    <CqSelectInput noMargin label="Standaard dag om rooster te openen" :options="startDateDefaultTypeOptions" v-model="startDateDefaultType" />
                </ListItem>
            </List>
        </template>
    </OpenableMenu>
</template>

<script lang="ts">
    import {Component, Vue, Prop} from 'vue-facing-decorator';
    import {CqToggle, CqSelectInput, StringSelectInputOption} from '@cquential/ui';
    import OpenableMenu from '@/components/Branding/OpenableMenu.vue';
    import ListItem from '@/components/Branding/ListItem.vue';
    import List from '@/components/Branding/List.vue';
    import {WorkloadRequirementSorting} from '@/types/enums/WorkloadRequirementSorting';
    import {ViewOptions} from '@/types/types';
    import {StartDateDefaultType} from '@/types/enums/StartDateDefaultType';

    @Component({
        components: {
            CqToggle,
            CqSelectInput,
            OpenableMenu,
            ListItem,
            List,
        },
        emits: ['update:preference', 'update:viewOptions', 'open:themeSettings'],
    })
    export default class DisplayOptionsMenu extends Vue {
        @Prop({required: true}) public viewOptions!: ViewOptions;
        @Prop({required: true}) public preferences!: Record<string, string | number>;
        @Prop({type: Boolean}) public areWorkSchedulesEnabled!: boolean;

        private static readonly requirementSortingOptions: StringSelectInputOption[] = [
            {value: WorkloadRequirementSorting.Alphabet, label: 'Alfabet'},
            {value: WorkloadRequirementSorting.BeginTime, label: 'Begintijd dienst'},
        ];

        private static readonly startDateDefaultTypeOptions: StringSelectInputOption[] = [
            {value: StartDateDefaultType.ThisWeekMonday, label: 'Maandag van huidige week'},
            {value: StartDateDefaultType.ThreeDaysBeforeToday, label: 'Drie dagen voor vandaag'},
            {value: StartDateDefaultType.Today, label: 'Vandaag'},
            {value: StartDateDefaultType.FirstDayNotPublished, label: 'Eerste ongepubliceerde dag'},
        ];

        public get requirementSortingOptions(): StringSelectInputOption[] {
            return DisplayOptionsMenu.requirementSortingOptions;
        }

        public get startDateDefaultTypeOptions(): StringSelectInputOption[] {
            return DisplayOptionsMenu.startDateDefaultTypeOptions;
        }

        public get showOnlyNegativeValues(): boolean {
            return this.viewOptions.showOnlyNegativeValues;
        }

        public set showOnlyNegativeValues(val: boolean) {
            this.$emit('update:viewOptions', 'showOnlyNegativeValues', val);
            this.$emit('update:preference', 'showOnlyNegativeValues', val);
        }

        public get showFunctions(): boolean {
            return this.viewOptions.showFunctions;
        }

        public set showFunctions(val: boolean) {
            this.$emit('update:viewOptions', 'showFunctions', val);
            this.$emit('update:preference', 'showFunctions', val);
        }

        public get showComments(): boolean {
            return this.viewOptions.showComments;
        }

        public set showComments(val: boolean) {
            this.$emit('update:viewOptions', 'showComments', val);
            this.$emit('update:preference', 'showComments', val);
        }

        public get showStampStartDate(): boolean {
            return this.viewOptions.showStampStartDate;
        }

        public set showStampStartDate(val: boolean) {
            this.$emit('update:viewOptions', 'showStampStartDate', val);
            this.$emit('update:preference', 'showStampStartDate', val);
        }

        public get showCompactMode(): boolean {
            return this.viewOptions.showCompactMode;
        }

        public set showCompactMode(val: boolean) {
            this.$emit('update:viewOptions', 'showCompactMode', val);
            this.$emit('update:preference', 'showCompactMode', val);
        }

        public get hideTooltip(): boolean {
            return this.viewOptions.hideTooltip;
        }

        public set hideTooltip(val: boolean) {
            this.$emit('update:viewOptions', 'hideTooltip', val);
            this.$emit('update:preference', 'hideTooltip', val);
        }

        public get useNewWorkloadRequirements(): boolean {
            return this.viewOptions.useNewWorkloadRequirements;
        }

        public set useNewWorkloadRequirements(val: boolean) {
            this.$emit('update:viewOptions', 'useNewWorkloadRequirements', val);
            this.$emit('update:preference', 'useNewWorkloadRequirements', val);
        }

        public get requirementSorting(): WorkloadRequirementSorting {
            return 'requirementSorting' in this.preferences ? this.preferences.requirementSorting as WorkloadRequirementSorting : WorkloadRequirementSorting.Alphabet;
        }

        public set requirementSorting(val: WorkloadRequirementSorting) {
            this.$emit('update:preference', 'requirementSorting', val);
        }

        public get startDateDefaultType(): StartDateDefaultType {
            return 'startDateDefaultType' in this.preferences ? this.preferences.startDateDefaultType as StartDateDefaultType : StartDateDefaultType.ThreeDaysBeforeToday;
        }

        public set startDateDefaultType(val: StartDateDefaultType) {
            this.$emit('update:preference', 'startDateDefaultType', val);
        }
    }
</script>
